import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import type { AnnouncementLinkedSet } from '../../services/announcements.service'
import { formatSetCode } from '../../utils/setCode'
import { SetImage } from '../ui/SetImage'
import { getSetAccentStyle } from '../ui/CardInfoPanel'
import styles from './SetAnnouncementModal.module.css'

interface SetAnnouncementModalProps {
  isOpen: boolean
  linkedSet: AnnouncementLinkedSet | null
  message?: string | null
  onClose: () => void
}

export function SetAnnouncementModal({ isOpen, linkedSet, message, onClose }: SetAnnouncementModalProps) {
  const navigate = useNavigate()

  const setTypeLabel = useMemo(() => {
    if (!linkedSet?.setType) return null
    return String(linkedSet.setType).charAt(0).toUpperCase() + String(linkedSet.setType).slice(1)
  }, [linkedSet?.setType])

  const releaseLabel = useMemo(() => {
    if (!linkedSet?.releaseDate) return null
    return new Date(linkedSet.releaseDate).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }, [linkedSet?.releaseDate])

  const totalCards = useMemo(() => {
    if (!linkedSet) return null
    if (Number.isFinite(Number(linkedSet.actualTotal))) return Number(linkedSet.actualTotal)
    if (Number.isFinite(Number(linkedSet.printedTotal))) return Number(linkedSet.printedTotal)
    return null
  }, [linkedSet])

  if (!isOpen) return null

  const openInventoryAdd = () => {
    if (!linkedSet?.id) return
    const params = new URLSearchParams()
    params.set('mode', 'catalog')
    params.set('q', '')
    params.set('setId', linkedSet.id)
    params.set('sort', 'set_number_asc')
    if (linkedSet.gameId) params.set('gameId', linkedSet.gameId)
    navigate(`/search/cards?${params.toString()}`)
    onClose()
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="announcement-set-title">
        <div className={styles.header}>
          <h3 id="announcement-set-title" className={styles.title}>Upcoming Set</h3>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close set details">
            <X size={18} />
          </button>
        </div>

        <div className={styles.body}>
          {message ? <p className={styles.announcementText}>{message}</p> : null}

          {linkedSet ? (
            <>
              <div className={styles.hero} style={getSetAccentStyle(linkedSet.setCode)}>
                <div className={styles.media}>
                  <SetImage
                    logoUrl={linkedSet.logoUrl}
                    symbolUrl={linkedSet.symbolUrl}
                    coverImageUrl={linkedSet.coverImageUrl}
                    setCode={linkedSet.setCode}
                    setName={linkedSet.setName}
                    alt={linkedSet.setName}
                  />
                </div>
                <div className={styles.heroText}>
                  <span className={styles.eyebrow}>Releasing soon</span>
                  <h4 className={styles.setName}>{linkedSet.setName}</h4>
                  <div className={styles.metaLine}>
                    {formatSetCode(linkedSet.setCode) ? <span className={styles.chip}>{formatSetCode(linkedSet.setCode)}</span> : null}
                    {linkedSet.gameName ? (
                      <span className={styles.chip}>
                        {linkedSet.gameName}
                      </span>
                    ) : null}
                    {linkedSet.language && linkedSet.language !== 'en' ? <span className={styles.chip}>{linkedSet.language.toUpperCase()}</span> : null}
                  </div>
                </div>
              </div>

              <div className={styles.metaGrid}>
                {setTypeLabel ? (
                  <div className={styles.metaCard}>
                    <span className={styles.metaLabel}>Type</span>
                    <div className={styles.metaValue}>{setTypeLabel}</div>
                  </div>
                ) : null}
                {releaseLabel ? (
                  <div className={styles.metaCard}>
                    <span className={styles.metaLabel}>Release Date</span>
                    <div className={styles.metaValue}>{releaseLabel}</div>
                  </div>
                ) : null}
                {totalCards !== null ? (
                  <div className={styles.metaCard}>
                    <span className={styles.metaLabel}>Total Cards</span>
                    <div className={styles.metaValue}>{totalCards}</div>
                  </div>
                ) : null}
                {Number.isFinite(Number(linkedSet.printedTotal)) ? (
                  <div className={styles.metaCard}>
                    <span className={styles.metaLabel}>Printed Total</span>
                    <div className={styles.metaValue}>{Number(linkedSet.printedTotal)}</div>
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div className={styles.error}>Set details are unavailable for this announcement.</div>
          )}

          <div className={styles.actions}>
            <button type="button" className={styles.secondaryButton} onClick={onClose}>Close</button>
            <button type="button" className={styles.primaryButton} onClick={openInventoryAdd} disabled={!linkedSet?.id}>
              Open in Inventory/Add
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SetAnnouncementModal